import React, { Fragment, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { gql, useClient, useMutation, useQuery } from "urql";
import { useLocation, useParams, useSearchParams } from "wouter";
import { cloneDeep, keyBy, sortBy } from "lodash";
import * as XLSX from "xlsx";
import { FileSpreadsheet } from "lucide-react";

import { DateHelper, emptyObjectId } from "../../../common/src";
import { bootstrapDiscountCurveWithUfr, type Pillar } from "../../../common/src/bootstrapDiscountCurve";
import { genericLiability } from "../../../common/src/genericLiability";
import { priceFormat, twoDecPriceFormat } from "../../../common/src/utils/FormatFunctions";
import { formikUrqlErrorFormater } from "../../../components/src/common/formik-urql-error-helper";
import { exportToXlsx, sheetType } from "../../../components/src/common/exportToXlsx";
import { usePrevious } from "../../../components/src/common/Utils";
import { serializeSwedenDate } from "../../../components/src/dateFormater";
import { Column, Grid } from "../../../components/src";
import { DurationCurveChart } from "../../../components/src/charts/DurationCurveChart";
import { Dropdown } from "../../../components/src/Dropdown";
import { DateForm } from "../../../components/src/dateForm";
import { Page } from "../../../components/src/Page";
import { TableGrouper } from "../../../components/src/react-table/TableGrouper";
import { useQueryState } from "../../../components/src/use-query-state";
import { ClientContext } from "../../../components/src/contexts/ClientContext";
import { ClientContextSelector } from "../contexts/ClientContextSelector";
import { YesNoModal } from "../../../components/src/YesNoModal";
import { CreateReportInput, Instrument, PartyStatusEnum, PartyType, ReportStatusEnum, TransactionFilterInput } from "../types.generated";

const CREATE_TPT_REPORT = gql`
    mutation createTptReport($input: CreateReportInput!) {
        report: createReport(input: $input) {
            _id
            name
            clientId
            date
        }
    }
`;

interface TptRow {
    [key: string]: string | number | boolean | null;
}

type DurationBucket = "lt5y" | "between5y15y" | "gt15y";

interface StressParameters {
    a: Record<DurationBucket, number | null>;
    b: Record<DurationBucket, number>;
}

interface ExposureSummaryRow {
    creditRating: string;
    sum: number;
    stressedSum: number;
    weightedStressPercent: number | null;
}

const _CREDIT_STRESS_TABLE: Record<string, StressParameters> = {
    SOBL: {
        a: { lt5y: null, between5y15y: 4.5, gt15y: 10.5 },
        b: { lt5y: 0.9, between5y15y: 0.6, gt15y: 0.3 }
    },
    "0": {
        a: { lt5y: null, between5y15y: 5, gt15y: 11 },
        b: { lt5y: 1, between5y15y: 0.6, gt15y: 0.3 }
    },
    "1": {
        a: { lt5y: null, between5y15y: 6, gt15y: 12 },
        b: { lt5y: 1.2, between5y15y: 0.6, gt15y: 0.3 }
    },
    "2": {
        a: { lt5y: null, between5y15y: 7, gt15y: 13 },
        b: { lt5y: 1.4, between5y15y: 0.6, gt15y: 0.3 }
    },
    "3": {
        a: { lt5y: null, between5y15y: 9, gt15y: 15 },
        b: { lt5y: 1.8, between5y15y: 0.6, gt15y: 0.3 }
    },
    "4/5": {
        a: { lt5y: null, between5y15y: 16, gt15y: 22 },
        b: { lt5y: 3.2, between5y15y: 0.6, gt15y: 0.3 }
    },
    "6": {
        a: { lt5y: null, between5y15y: 25, gt15y: 31 },
        b: { lt5y: 5, between5y15y: 0.6, gt15y: 0.3 }
    },
    Inget_kreditbetyg: {
        a: { lt5y: null, between5y15y: 10, gt15y: 16 },
        b: { lt5y: 2, between5y15y: 0.6, gt15y: 0.3 }
    }
};

function parseNumericCell(value: string | number | boolean | null): number | null {
    const normalizedValue = String(value ?? "")
        .trim()
        .replace(/\s/g, "")
        .replace(",", ".");
    const parsedValue = Number(normalizedValue);
    return Number.isFinite(parsedValue) ? parsedValue : null;
}

const TPT_ISIN_COLUMN = "14_Identification_code_of_the_instrument";
const TPT_PORTFOLIO_ID_COLUMN = "1_Portfolio_identifying_data";
const TPT_VALUATION_DATE_COLUMN = "6_Valuation_date";
const TPT_SHARE_PRICE_COLUMN = "8_Share_price";
const TPT_TOTAL_SHARES_COLUMN = "8b_Total_number_of_shares";
const TPT_CREDIT_SENSITIVITY_COLUMN = "92_Credit_sensitivity";
const TPT_MARKET_EXPOSURE_COLUMN = "28_Market_exposure_amount_in_portfolio_currency_(B)";
const TPT_MARKET_VALUATION_COLUMN = "24_Market_valuation_in_portfolio_currency_(B)";
const TPT_MODIFIED_DURATION_COLUMN = "90_Modified_duration_to_maturity_date";
const TPT_CIC_CODE_COLUMN = "12_CIC_code_of_the_instrument";
const TPT_QUOTATION_CURRENCY_COLUMN = "21_Quotation_currency_(A)";
const TPT_UNDERLYING_CIC_COLUMN = "67_CIC_of_the_underlying_asset";
const FX_STRESS_UNDERLYING_CIC_EXCEPTION = ["XLF1", "XLF4"];
const EQUITY_STRESS_MARKET_EXPOSURE_FACTOR = 0.3;
const FX_STRESS_MARKET_EXPOSURE_FACTOR = 0.1;
const POSITIONS_ROOT_NAME = "Total";

const REGULATORY_STRESS_CORRELATION_MATRIX: number[][] = [
    [1, 0.5, 0.5, 0.5, 0, 0, 0.5],
    [0.5, 1, 0.75, 0.5, 0, 0, 0.75],
    [0.5, 0.75, 1, 0.5, 0, 0, 0.75],
    [0.5, 0.5, 0.5, 1, 0, 0, 0.5],
    [0, 0, 0, 0, 1, 0, 0],
    [0, 0, 0, 0, 0, 1, 0],
    [0.5, 0.75, 0.75, 0.5, 0, 0, 1]
];

function calculateCorrelatedTotalStress(stressValues: number[], correlationMatrix: number[][]): number {
    let sum = 0;

    for (let rowIndex = 0; rowIndex < stressValues.length; rowIndex++) {
        for (let columnIndex = 0; columnIndex < stressValues.length; columnIndex++) {
            sum += correlationMatrix[rowIndex][columnIndex] * stressValues[rowIndex] * stressValues[columnIndex];
        }
    }

    return Math.sqrt(Math.max(0, sum));
}

function getTptRowStressedAmount(row: TptRow): number {
    const amount = parseNumericCell(row["24_Market_valuation_in_portfolio_currency_(B)"]);
    if (amount === null) {
        return 0;
    }

    const coveredFlag = String(row["55_Covered_not_covered"] ?? "").trim();
    let stressLookupStep = String(row["59_Credit_quality_step"] ?? "").trim();
    const creditRatingRaw = String(row["112_Credit_rating"] ?? "").trim();
    if (coveredFlag === "C") {
        stressLookupStep = "SOBL";
    }
    if (creditRatingRaw.toLowerCase() === "not rated") {
        stressLookupStep = "Inget_kreditbetyg";
    }
    if (!stressLookupStep) {
        stressLookupStep = "Inget_kreditbetyg";
    }

    const creditDuration = parseNumericCell(row[TPT_CREDIT_SENSITIVITY_COLUMN]);
    const cicCode = String(row["12_CIC_code_of_the_instrument"] ?? "").trim();
    const hasZeroStressCicCode = cicCode.endsWith("11");
    const stressPercent = hasZeroStressCicCode
        ? 0
        : creditDuration === null
          ? null
          : calculateStressPercent(stressLookupStep, creditDuration);
    if (stressPercent === null) {
        return 0;
    }

    return amount * (stressPercent / 100);
}

function normalizeIsin(value: string | number | boolean | null | undefined): string {
    return String(value ?? "")
        .trim()
        .toUpperCase();
}

function getReportTotalShares(rows: TptRow[], reportName: string): number | null {
    const normalizedReportName = normalizeIsin(reportName);

    for (const row of rows) {
        const rowIsin = normalizeIsin(row[TPT_ISIN_COLUMN]);
        const rowPortfolioId = normalizeIsin(row[TPT_PORTFOLIO_ID_COLUMN]);
        if (rowIsin !== normalizedReportName && rowPortfolioId !== normalizedReportName) {
            continue;
        }

        const totalShares = parseNumericCell(row[TPT_TOTAL_SHARES_COLUMN]);
        if (totalShares !== null && totalShares !== 0) {
            return totalShares;
        }
    }

    for (const row of rows) {
        const totalShares = parseNumericCell(row[TPT_TOTAL_SHARES_COLUMN]);
        if (totalShares !== null && totalShares !== 0) {
            return totalShares;
        }
    }

    return null;
}

function getReportSharePrice(rows: TptRow[], reportName: string): number | null {
    const normalizedReportName = normalizeIsin(reportName);

    for (const row of rows) {
        const rowIsin = normalizeIsin(row[TPT_ISIN_COLUMN]);
        const rowPortfolioId = normalizeIsin(row[TPT_PORTFOLIO_ID_COLUMN]);
        if (rowIsin !== normalizedReportName && rowPortfolioId !== normalizedReportName) {
            continue;
        }

        const sharePrice = parseNumericCell(row[TPT_SHARE_PRICE_COLUMN]);
        if (sharePrice !== null && sharePrice !== 0) {
            return sharePrice;
        }
    }

    for (const row of rows) {
        const sharePrice = parseNumericCell(row[TPT_SHARE_PRICE_COLUMN]);
        if (sharePrice !== null && sharePrice !== 0) {
            return sharePrice;
        }
    }

    return null;
}

function scaleCreditStressByPositionQuantity(creditStress: number, quantity: unknown, totalShares: number | null): number | null {
    const positionQuantity = parseNumericCell(quantity as string | number | boolean | null);
    if (positionQuantity === null || totalShares === null || totalShares === 0) {
        return null;
    }

    return creditStress * (positionQuantity / totalShares);
}

function scaleStressByPositionQuantityAndSharePrice(
    stress: number,
    quantity: unknown,
    totalShares: number | null,
    positionPrice: unknown,
    reportSharePrice: number | null
): number | null {
    const scaledByQuantity = scaleCreditStressByPositionQuantity(stress, quantity, totalShares);
    if (scaledByQuantity === null) {
        return null;
    }

    const parsedPositionPrice = parseNumericCell(positionPrice as string | number | boolean | null);
    if (parsedPositionPrice === null || reportSharePrice === null || reportSharePrice === 0) {
        return null;
    }

    return scaledByQuantity * (parsedPositionPrice / reportSharePrice);
}

function buildTptCreditStressByIsin(rows: TptRow[]): { creditStressByIsin: Record<string, number>; creditStressTotal: number } {
    const creditStressByIsin: Record<string, number> = {};
    let creditStressTotal = 0;

    rows.forEach((row) => {
        const stressedAmount = getTptRowStressedAmount(row);

        creditStressTotal += stressedAmount;

        const isin = normalizeIsin(row[TPT_ISIN_COLUMN]);
        if (!isin) {
            return;
        }

        creditStressByIsin[isin] = (creditStressByIsin[isin] ?? 0) + stressedAmount;
    });

    return { creditStressByIsin, creditStressTotal };
}

function calculateStressPercent(creditStep: string, creditDuration: number): number | null {
    const normalizedCreditStep = String(creditStep || "").trim() || "Inget_kreditbetyg";
    const stressParameters = _CREDIT_STRESS_TABLE[normalizedCreditStep];
    if (typeof stressParameters === "undefined") {
        return null;
    }

    if (creditDuration < 5) {
        return stressParameters.b.lt5y * creditDuration;
    }

    if (creditDuration <= 15) {
        const a = stressParameters.a.between5y15y;
        if (a === null) {
            return null;
        }
        return a + stressParameters.b.between5y15y * (creditDuration - 5);
    }

    const a = stressParameters.a.gt15y;
    if (a === null) {
        return null;
    }
    return a + stressParameters.b.gt15y * (creditDuration - 15);
}

function getLatestPriceCloseCutoffDate(date: string): string | null {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return null;
    }

    return serializeSwedenDate(DateHelper.addDays(new Date(date), -1));
}

function getTptReportName(fileName: string): string {
    if (!fileName) {
        return "tpt";
    }

    return fileName.replace(/\.(xlsx|xls)$/i, "");
}

function getTptReportMetadata(rows: TptRow[]): { name: string; date: string } | null {
    const firstRow = rows[0];
    if (!firstRow) {
        return null;
    }

    const name = String(firstRow[TPT_PORTFOLIO_ID_COLUMN] ?? "").trim();
    const dateValue = firstRow[TPT_VALUATION_DATE_COLUMN];
    if (!name || dateValue === null || typeof dateValue === "undefined" || String(dateValue).trim() === "") {
        return null;
    }

    const date = serializeSwedenDate(dateValue);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return null;
    }

    return { name, date };
}

function isTptReportDateLaterThanLatest(newDate: string, latestSavedDate: string | undefined): boolean {
    if (!latestSavedDate) {
        return true;
    }

    return newDate > latestSavedDate;
}

const GET_POSITIONS_GROUPED_DATA = gql`
    query getData($filter: PositionFilter) {
        me {
            roles {
                _id
                assets
                clientIds
                permissionType
                name
            }
        }

        tradingmanager(filterZeroPositions: true, filter: $filter, lookThrough: false) {
            instrumentId
            name
            longName
            isin
            quantity
            currency
            exposure
            localExposure
            valuationPrice
            valuationDate
            valuationAccruedInterest
            valuationCleanPrice
            valuationCurrency
            fxRate
            bloombergTicker
            isCashAccount
            accountName
            instrument {
                type
                creditRating
                modelType
                modelNotionalScaling
                productType
                countryOfListing
            }
            issuerName
            issuerId
            issuerLei
            issuerType
            issuerIndustryCode
        }
    }
`;

const GET_TPT_REPORTS_BY_NAME = gql`
    query tptReportsByName($clientId: GraphQLObjectId!, $nameIn: [String!]) {
        reports(clientId: $clientId, type: "TPT", nameIn: $nameIn, statusIn: [Active]) {
            name
            date
        }
    }
`;

const GET_CLIENT_TPT_REPORTS = gql`
    query clientTptReports($clientId: GraphQLObjectId!) {
        reports(clientId: $clientId, type: "TPT", statusIn: [Active]) {
            name
            date
            data
        }
    }
`;

const IRS_INSTRUMENT_NAMES = [
    "SEK/IRS/3M/1Y",
    "SEK/IRS/3M/2Y",
    "SEK/IRS/3M/3Y",
    "SEK/IRS/3M/4Y",
    "SEK/IRS/3M/5Y",
    "SEK/IRS/3M/6Y",
    "SEK/IRS/3M/7Y",
    "SEK/IRS/3M/8Y",
    "SEK/IRS/3M/9Y",
    "SEK/IRS/3M/10Y",
    "SEK/IRS/3M/12Y",
    "SEK/IRS/3M/15Y",
    "SEK/IRS/3M/20Y"
];

const IRS_DURATION_BY_INSTRUMENT: Record<string, number> = {
    "SEK/IRS/3M/1Y": 1,
    "SEK/IRS/3M/2Y": 2,
    "SEK/IRS/3M/3Y": 3,
    "SEK/IRS/3M/4Y": 4,
    "SEK/IRS/3M/5Y": 5,
    "SEK/IRS/3M/6Y": 6,
    "SEK/IRS/3M/7Y": 7,
    "SEK/IRS/3M/8Y": 8,
    "SEK/IRS/3M/9Y": 9,
    "SEK/IRS/3M/10Y": 10,
    "SEK/IRS/3M/12Y": 12,
    "SEK/IRS/3M/15Y": 15,
    "SEK/IRS/3M/20Y": 20
};

const IRS_ABSOLUTE_SHIFT_BY_INSTRUMENT: Record<string, number> = {
    "SEK/IRS/3M/1Y": 50,
    "SEK/IRS/3M/2Y": 53,
    "SEK/IRS/3M/3Y": 56,
    "SEK/IRS/3M/4Y": 60,
    "SEK/IRS/3M/5Y": 62,
    "SEK/IRS/3M/6Y": 64,
    "SEK/IRS/3M/7Y": 65,
    "SEK/IRS/3M/8Y": 66,
    "SEK/IRS/3M/9Y": 67,
    "SEK/IRS/3M/10Y": 68,
    "SEK/IRS/3M/12Y": 69,
    "SEK/IRS/3M/15Y": 70,
    "SEK/IRS/3M/20Y": 70
};

const IRS_RELATIVE_SHIFT_BY_INSTRUMENT: Record<string, number> = {
    "SEK/IRS/3M/1Y": 0.41,
    "SEK/IRS/3M/2Y": 0.38,
    "SEK/IRS/3M/3Y": 0.36,
    "SEK/IRS/3M/4Y": 0.33,
    "SEK/IRS/3M/5Y": 0.32,
    "SEK/IRS/3M/6Y": 0.3,
    "SEK/IRS/3M/7Y": 0.28,
    "SEK/IRS/3M/8Y": 0.27,
    "SEK/IRS/3M/9Y": 0.26,
    "SEK/IRS/3M/10Y": 0.25,
    "SEK/IRS/3M/12Y": 0.23,
    "SEK/IRS/3M/15Y": 0.22,
    "SEK/IRS/3M/20Y": 0.2
};

function buildRegulatoryCurvePillars(priceByDuration: Record<number, number>): Pillar[] {
    const knownDurations = Object.keys(priceByDuration)
        .map(Number)
        .sort((a, b) => a - b);

    if (knownDurations.length === 0) {
        return [];
    }

    const pillarsByDuration: Record<number, number> = { ...priceByDuration };

    for (let index = 0; index < knownDurations.length - 1; index++) {
        const startDuration = knownDurations[index];
        const endDuration = knownDurations[index + 1];
        const startPrice = priceByDuration[startDuration];
        const endPrice = priceByDuration[endDuration];

        for (let duration = startDuration + 1; duration < endDuration; duration++) {
            const weight = (duration - startDuration) / (endDuration - startDuration);
            pillarsByDuration[duration] = startPrice + weight * (endPrice - startPrice);
        }
    }
    console.log("pillarsByDuration", pillarsByDuration);
    return Object.keys(pillarsByDuration)
        .map(Number)
        .sort((a, b) => a - b)
        .map((duration) => ({ t: duration, parRate: pillarsByDuration[duration] }));
}

function buildInterpolatedShiftByDuration(shiftByDuration: Record<number, number>): Record<number, number> {
    const knownDurations = Object.keys(shiftByDuration)
        .map(Number)
        .sort((a, b) => a - b);

    if (knownDurations.length === 0) {
        return {};
    }

    const interpolatedByDuration: Record<number, number> = { ...shiftByDuration };

    for (let index = 0; index < knownDurations.length - 1; index++) {
        const startDuration = knownDurations[index];
        const endDuration = knownDurations[index + 1];
        const startShift = shiftByDuration[startDuration];
        const endShift = shiftByDuration[endDuration];

        for (let duration = startDuration + 1; duration < endDuration; duration++) {
            const weight = (duration - startDuration) / (endDuration - startDuration);
            interpolatedByDuration[duration] = startShift + weight * (endShift - startShift);
        }
    }

    const maxDuration = knownDurations[knownDurations.length - 1];
    const maxShift = shiftByDuration[maxDuration];
    for (let duration = maxDuration + 1; duration <= 50; duration++) {
        interpolatedByDuration[duration] = maxShift;
    }

    return interpolatedByDuration;
}

function getLiabilityLeafOriginal(row: {
    original?: { isLiabilityRow?: boolean };
    subRows?: { original?: { isLiabilityRow?: boolean }; subRows?: any[] }[];
}): {
    isLiabilityRow?: boolean;
    exposure?: number | null;
    relativeIrStress?: number | null;
    absoluteIrStress?: number | null;
} | null {
    if (row.original?.isLiabilityRow) {
        return row.original;
    }

    for (const subRow of row.subRows ?? []) {
        const liabilityLeaf = getLiabilityLeafOriginal(subRow);
        if (liabilityLeaf) {
            return liabilityLeaf;
        }
    }

    return null;
}

function renderLiabilityAggregatedValue(
    row: { original?: { isLiabilityRow?: boolean }; subRows?: { original?: { isLiabilityRow?: boolean }; subRows?: any[] }[] },
    field: "exposure" | "relativeIrStress" | "absoluteIrStress"
): React.ReactElement | null {
    const liabilityLeaf = getLiabilityLeafOriginal(row);
    if (!liabilityLeaf) {
        return null;
    }

    const value = liabilityLeaf[field];
    return <div style={{ textAlign: "right" }}>{value === null || typeof value === "undefined" ? "-" : twoDecPriceFormat(value)}</div>;
}

function renderPositionsTopNodeAggregatedValue(value: number | null | undefined): React.ReactElement {
    return <div style={{ textAlign: "right" }}>{value === null || typeof value === "undefined" ? "-" : twoDecPriceFormat(value)}</div>;
}

function renderPositionsTopNodeTotalStressCell(totalStress: number, exposure: number): React.ReactElement {
    let color: string | undefined;
    if (totalStress > exposure) {
        color = "red";
    } else if (totalStress < exposure) {
        color = "green";
    }

    return <div style={{ textAlign: "right", color }}>{twoDecPriceFormat(totalStress)}</div>;
}

function getAbsoluteShiftForModifiedDuration(modifiedDuration: number): number | null {
    let absoluteShift: number | null = null;
    let matchedDuration: number | null = null;

    IRS_INSTRUMENT_NAMES.forEach((instrumentName) => {
        const irsDuration = IRS_DURATION_BY_INSTRUMENT[instrumentName];
        if (irsDuration <= modifiedDuration) {
            return;
        }

        if (matchedDuration === null || irsDuration < matchedDuration) {
            matchedDuration = irsDuration;
            absoluteShift = IRS_ABSOLUTE_SHIFT_BY_INSTRUMENT[instrumentName];
        }
    });
    console.log("absoluteShift", absoluteShift);
    return absoluteShift;
}

function calculateTptReportAbsoluteIrStress(rows: TptRow[]): number {
    let total = 0;

    rows.forEach((row) => {
        const marketExposure = parseNumericCell(row[TPT_MARKET_EXPOSURE_COLUMN]);
        const modifiedDuration = parseNumericCell(row[TPT_MODIFIED_DURATION_COLUMN]);
        if (marketExposure === null || modifiedDuration === null) {
            return;
        }

        const absoluteShift = getAbsoluteShiftForModifiedDuration(modifiedDuration);
        if (absoluteShift === null) {
            return;
        }

        total += (marketExposure * modifiedDuration * absoluteShift) / 10000;
    });

    return total;
}

function getRelativeShiftBpForModifiedDuration(
    modifiedDuration: number,
    relativeShiftBpByInstrumentName: Record<string, number>
): number | null {
    let relativeShiftBp: number | null = null;
    let matchedDuration: number | null = null;

    IRS_INSTRUMENT_NAMES.forEach((instrumentName) => {
        const irsDuration = IRS_DURATION_BY_INSTRUMENT[instrumentName];
        if (irsDuration <= modifiedDuration) {
            return;
        }

        const instrumentRelativeShiftBp = relativeShiftBpByInstrumentName[instrumentName];
        if (typeof instrumentRelativeShiftBp === "undefined") {
            return;
        }

        if (matchedDuration === null || irsDuration < matchedDuration) {
            matchedDuration = irsDuration;
            relativeShiftBp = instrumentRelativeShiftBp;
        }
    });

    return relativeShiftBp;
}

function calculateTptReportRelativeIrStress(rows: TptRow[], relativeShiftBpByInstrumentName: Record<string, number>): number {
    let total = 0;

    rows.forEach((row) => {
        const marketExposure = parseNumericCell(row[TPT_MARKET_EXPOSURE_COLUMN]);
        const modifiedDuration = parseNumericCell(row[TPT_MODIFIED_DURATION_COLUMN]);
        if (marketExposure === null || modifiedDuration === null) {
            return;
        }

        const relativeShiftBp = getRelativeShiftBpForModifiedDuration(modifiedDuration, relativeShiftBpByInstrumentName);
        if (relativeShiftBp === null) {
            return;
        }

        total += (marketExposure * modifiedDuration * relativeShiftBp) / 10000;
    });

    return total;
}

function getTptRowEquityStress(row: TptRow): number {
    const cicCode = String(row[TPT_CIC_CODE_COLUMN] ?? "").trim();
    if (cicCode.length < 3 || cicCode.charAt(2) !== "3") {
        return 0;
    }

    const marketExposure = parseNumericCell(row[TPT_MARKET_EXPOSURE_COLUMN]);
    if (marketExposure === null) {
        return 0;
    }

    return EQUITY_STRESS_MARKET_EXPOSURE_FACTOR * marketExposure;
}

function calculateTptReportEquityStress(rows: TptRow[]): number {
    let total = 0;

    rows.forEach((row) => {
        total += getTptRowEquityStress(row);
    });

    return total;
}

function getTptRowFxStress(row: TptRow): number {
    const quotationCurrency = String(row[TPT_QUOTATION_CURRENCY_COLUMN] ?? "")
        .trim()
        .toUpperCase();
    if (quotationCurrency === "SEK") {
        return 0;
    }

    const underlyingCic = String(row[TPT_UNDERLYING_CIC_COLUMN] ?? "")
        .trim()
        .toUpperCase();
    const amountColumn = FX_STRESS_UNDERLYING_CIC_EXCEPTION.includes(underlyingCic)
        ? TPT_MARKET_VALUATION_COLUMN
        : TPT_MARKET_EXPOSURE_COLUMN;
    const amount = parseNumericCell(row[amountColumn]);
    if (amount === null) {
        return 0;
    }

    return FX_STRESS_MARKET_EXPOSURE_FACTOR * amount;
}

function calculateTptReportFxStress(rows: TptRow[]): number {
    let total = 0;

    rows.forEach((row) => {
        total += getTptRowFxStress(row);
    });

    return total;
}

const GET_IR_INSTRUMENT_PRICES = gql`
    query irInstrumentPrices($instrumentNames: [String!]!, $endDate: GraphQLDateString!) {
        instruments(nameIn: $instrumentNames) {
            _id
            name
        }
        timeseries(
            includeItems: true
            lastItem: true
            filter: { instrumentNameIn: $instrumentNames, typeIn: ["Price(Close)"], endDate: $endDate }
        ) {
            dates
            values
            instrument {
                _id
                name
            }
        }
    }
`;

const GET_POSITIONS_GROUPED_CLIENTS = gql`
    query {
        clients: parties(filter: { typeIn: [Fund, Client] }) {
            _id
            name
            accountingCurrency
            instruments {
                _id
                productType
            }
        }
    }
`;

const getNotional = (quantity, instrument) => {
    if (
        instrument &&
        (instrument.modelType === "Swap" || instrument.modelType === "Swaption") &&
        quantity > 0.9999999 &&
        quantity < 1.0000001
    ) {
        return instrument.modelNotionalScaling;
    }

    return quantity;
};

type PositionsTopNodeTotalsSnapshot = {
    exposure: number;
    totalStress: number;
};

interface PositionsGroupedPageProps {
    showClientSelector?: boolean;
    showTptReportDate?: boolean;
    uploadPanel?: React.ReactNode;
    tptReportRefreshKey?: number;
    onPositionsTopNodeTotalsChange?: (totals: PositionsTopNodeTotalsSnapshot) => void;
}

function PositionsGroupedPage({
    showClientSelector = true,
    showTptReportDate = false,
    uploadPanel,
    tptReportRefreshKey = 0,
    onPositionsTopNodeTotalsChange
}: PositionsGroupedPageProps): React.ReactElement {
    const { client: contextClient } = useContext(ClientContext);
    const { clientDashName, tabId } = useParams<{ clientDashName: string; tabId: string }>();
    const previousClientDashName = usePrevious(clientDashName);
    const previousTabId = usePrevious(tabId);
    const [location, navigate] = useLocation();

    const [date] = useQueryState("date", serializeSwedenDate(new Date(new Date().setDate(new Date().getDate() - 1))));
    const previousDate = usePrevious(date);
    const [queryArgs, setQueryArgs] = useSearchParams();

    const [selectedClient, setSelectedClient] = useState(null);
    const [filter, setFilter] = useState<TransactionFilterInput>(null);
    const [solvency, setSolvency] = useState("1.2");
    const previousSelectedClient = usePrevious(selectedClient);

    const [{ fetching: loadingClients, error: errorClients, data: dataClients }] = useQuery({
        query: GET_POSITIONS_GROUPED_CLIENTS,
        requestPolicy: "cache-and-network"
    });
    const [{ fetching: loading, error, data }] = useQuery({
        query: GET_POSITIONS_GROUPED_DATA,
        variables: { filter },
        pause: filter ? false : true,
        requestPolicy: "cache-and-network"
    });

    const processedData: { portfolioValue: number; allPositions: any[] } = useMemo(() => {
        if (selectedClient && data && data.tradingmanager && data.tradingmanager.length) {
            let selectedPartyInstruments: Record<string, Partial<Instrument>> = {};
            selectedPartyInstruments = keyBy(selectedClient.instruments, "_id");

            let portfolioValue = 0;
            let tradingManagerData = cloneDeep(data.tradingmanager);
            tradingManagerData = tradingManagerData.map((position) => {
                position.clientName = selectedClient.label;
                position.productType = position.instrument ? position.instrument.productType : null;

                portfolioValue += position.exposure;
                const { instrument, ...rest } = position;
                const notional = getNotional(rest.quantity, instrument);
                return { ...rest, ...instrument, notional };
            });

            const tradingManagerDataSorted = sortBy(tradingManagerData, "isCashAccount", "name");
            if (Object.keys(selectedPartyInstruments).length) {
                tradingManagerDataSorted.forEach((instrument) => {
                    if (!instrument.modelType && selectedPartyInstruments[instrument.instrumentId]) {
                        instrument.modelType = selectedPartyInstruments[instrument.instrumentId].productType;
                    }
                });
            }

            return { portfolioValue: portfolioValue, allPositions: tradingManagerDataSorted };
        }

        return null;
    }, [data, selectedClient]);

    const [{ data: clientTptReportsData }, reexecuteClientTptReports] = useQuery({
        query: GET_CLIENT_TPT_REPORTS,
        variables: {
            clientId: selectedClient?._id
        },
        pause: !showTptReportDate || !selectedClient,
        requestPolicy: "cache-and-network"
    });

    useEffect(() => {
        if (!showTptReportDate || !selectedClient || tptReportRefreshKey < 1) {
            return;
        }

        void reexecuteClientTptReports({ requestPolicy: "network-only" });
    }, [reexecuteClientTptReports, selectedClient, showTptReportDate, tptReportRefreshKey]);

    const latestClientTptReportsByName = useMemo(() => {
        const reportsByName = new Map<string, { name: string; date: string; data: unknown }>();
        if (!clientTptReportsData?.reports) {
            return reportsByName;
        }

        clientTptReportsData.reports.forEach((report) => {
            const reportName = String(report.name ?? "").trim();
            if (!reportName) {
                return;
            }

            const normalizedReportName = normalizeIsin(reportName);
            const existingReport = reportsByName.get(normalizedReportName);
            if (!existingReport || report.date > existingReport.date) {
                reportsByName.set(normalizedReportName, report);
            }
        });

        return reportsByName;
    }, [clientTptReportsData]);

    const savedTptRows = useMemo(() => {
        const rows: TptRow[] = [];

        latestClientTptReportsByName.forEach((report) => {
            if (!Array.isArray(report.data)) {
                return;
            }

            rows.push(...(report.data as TptRow[]));
        });

        return rows;
    }, [latestClientTptReportsByName]);

    const creditStressByIsin = useMemo(() => buildTptCreditStressByIsin(savedTptRows).creditStressByIsin, [savedTptRows]);

    const creditStressByReportName = useMemo(() => {
        const totals: Record<string, number> = {};

        latestClientTptReportsByName.forEach((report, reportName) => {
            if (!Array.isArray(report.data)) {
                return;
            }

            totals[normalizeIsin(reportName)] = buildTptCreditStressByIsin(report.data as TptRow[]).creditStressTotal;
        });

        return totals;
    }, [latestClientTptReportsByName]);

    const absoluteIrStressByReportName = useMemo(() => {
        const totals: Record<string, number> = {};

        latestClientTptReportsByName.forEach((report, reportName) => {
            if (!Array.isArray(report.data)) {
                return;
            }

            totals[normalizeIsin(reportName)] = calculateTptReportAbsoluteIrStress(report.data as TptRow[]);
        });

        return totals;
    }, [latestClientTptReportsByName]);

    const equityStressByReportName = useMemo(() => {
        const totals: Record<string, number> = {};

        latestClientTptReportsByName.forEach((report, reportName) => {
            if (!Array.isArray(report.data)) {
                return;
            }

            totals[normalizeIsin(reportName)] = calculateTptReportEquityStress(report.data as TptRow[]);
        });

        return totals;
    }, [latestClientTptReportsByName]);

    const fxStressByReportName = useMemo(() => {
        const totals: Record<string, number> = {};

        latestClientTptReportsByName.forEach((report, reportName) => {
            if (!Array.isArray(report.data)) {
                return;
            }

            totals[normalizeIsin(reportName)] = calculateTptReportFxStress(report.data as TptRow[]);
        });

        return totals;
    }, [latestClientTptReportsByName]);

    const priceCloseCutoffDate = useMemo(() => getLatestPriceCloseCutoffDate(date), [date]);

    const [{ fetching: loadingIrPrices, error: errorIrPrices, data: irPricesData }] = useQuery({
        query: GET_IR_INSTRUMENT_PRICES,
        variables: {
            instrumentNames: IRS_INSTRUMENT_NAMES,
            endDate: priceCloseCutoffDate
        },
        pause: !priceCloseCutoffDate,
        requestPolicy: "cache-and-network"
    });

    const relativeShiftBpByInstrumentName = useMemo(() => {
        const relativeShiftBpByInstrument: Record<string, number> = {};
        const priceByInstrumentName: Record<string, string> = {};

        if (irPricesData?.timeseries) {
            irPricesData.timeseries.forEach((series) => {
                const instrumentName = String(series.instrument?.name ?? "").trim();
                const priceValue = series.values?.[0];
                if (!instrumentName || typeof priceValue === "undefined") {
                    return;
                }

                priceByInstrumentName[instrumentName] = priceValue;
            });
        }

        IRS_INSTRUMENT_NAMES.forEach((instrumentName) => {
            const priceValue = priceByInstrumentName[instrumentName];
            if (typeof priceValue === "undefined") {
                return;
            }

            const parsedPrice = Number(priceValue);
            if (!Number.isFinite(parsedPrice)) {
                return;
            }

            relativeShiftBpByInstrument[instrumentName] = parsedPrice * IRS_RELATIVE_SHIFT_BY_INSTRUMENT[instrumentName] * 10000;
        });

        return relativeShiftBpByInstrument;
    }, [irPricesData]);

    const relativeIrStressByReportName = useMemo(() => {
        const totals: Record<string, number> = {};

        latestClientTptReportsByName.forEach((report, reportName) => {
            if (!Array.isArray(report.data)) {
                return;
            }

            totals[normalizeIsin(reportName)] = calculateTptReportRelativeIrStress(
                report.data as TptRow[],
                relativeShiftBpByInstrumentName
            );
        });

        return totals;
    }, [latestClientTptReportsByName, relativeShiftBpByInstrumentName]);

    const regulatoryCurve = useMemo(() => {
        const priceByDuration: Record<number, number> = {};

        IRS_INSTRUMENT_NAMES.forEach((instrumentName) => {
            const series = irPricesData?.timeseries?.find((item) => String(item.instrument?.name ?? "").trim() === instrumentName);
            const priceValue = series?.values?.[0];
            if (typeof priceValue === "undefined") {
                return;
            }

            const parsedPrice = Number(priceValue);
            if (!Number.isFinite(parsedPrice)) {
                return;
            }

            priceByDuration[IRS_DURATION_BY_INSTRUMENT[instrumentName]] = parsedPrice;
        });

        const pillars = buildRegulatoryCurvePillars(priceByDuration);
        if (pillars.length === 0) {
            return [];
        }

        return bootstrapDiscountCurveWithUfr(pillars, {
            ufr: 0.033,
            maxT: 60
        });
    }, [irPricesData]);

    const discountedGenericLiabilityTotal = useMemo(() => {
        if (regulatoryCurve.length === 0) {
            return null;
        }

        const discountByTime = keyBy(regulatoryCurve, "t");
        let total = 0;

        for (const liability of genericLiability) {
            const curvePoint = discountByTime[liability.time];
            if (!curvePoint) {
                return null;
            }

            total += (liability.value * 1) / Math.pow(1 + curvePoint.zeroRate, liability.time);
        }

        return total;
    }, [regulatoryCurve]);

    const scaledDiscountedGenericLiability = useMemo(() => {
        if (discountedGenericLiabilityTotal === null) {
            return null;
        }

        const solvencyValue = Number(solvency);
        if (!Number.isFinite(solvencyValue) || solvencyValue === 0) {
            return null;
        }

        const positionsTopValueSek = processedData?.portfolioValue;
        if (typeof positionsTopValueSek !== "number" || !Number.isFinite(positionsTopValueSek)) {
            return null;
        }

        return positionsTopValueSek / solvencyValue;
    }, [discountedGenericLiabilityTotal, processedData?.portfolioValue, solvency]);

    const scaledDiscountedGenericLiabilities = useMemo(() => {
        if (regulatoryCurve.length === 0 || discountedGenericLiabilityTotal === null || discountedGenericLiabilityTotal === 0) {
            return [];
        }

        const solvencyValue = Number(solvency);
        if (!Number.isFinite(solvencyValue) || solvencyValue === 0) {
            return [];
        }

        const positionsTopValueSek = processedData?.portfolioValue;
        if (typeof positionsTopValueSek !== "number" || !Number.isFinite(positionsTopValueSek)) {
            return [];
        }

        const scalingFactor = positionsTopValueSek / solvencyValue / discountedGenericLiabilityTotal;
        const discountByTime = keyBy(regulatoryCurve, "t");

        return genericLiability.flatMap((liability) => {
            const curvePoint = discountByTime[liability.time];
            if (!curvePoint) {
                return [];
            }

            const discountedValue = liability.value / Math.pow(1 + curvePoint.zeroRate, liability.time);

            return [
                {
                    time: liability.time,
                    value: discountedValue * scalingFactor
                }
            ];
        });
    }, [discountedGenericLiabilityTotal, processedData?.portfolioValue, regulatoryCurve, solvency]);

    const absoluteShiftByDuration = useMemo(() => {
        const shiftByDuration: Record<number, number> = {};

        IRS_INSTRUMENT_NAMES.forEach((instrumentName) => {
            shiftByDuration[IRS_DURATION_BY_INSTRUMENT[instrumentName]] = IRS_ABSOLUTE_SHIFT_BY_INSTRUMENT[instrumentName];
        });

        return buildInterpolatedShiftByDuration(shiftByDuration);
    }, []);

    const relativeShiftByDuration = useMemo(() => {
        const shiftByDuration: Record<number, number> = {};

        IRS_INSTRUMENT_NAMES.forEach((instrumentName) => {
            const relativeShiftBp = relativeShiftBpByInstrumentName[instrumentName];
            if (typeof relativeShiftBp === "undefined") {
                return;
            }

            shiftByDuration[IRS_DURATION_BY_INSTRUMENT[instrumentName]] = relativeShiftBp;
        });

        return buildInterpolatedShiftByDuration(shiftByDuration);
    }, [relativeShiftBpByInstrumentName]);

    const absoluteIrShiftScaledDiscountedGenericLiability = useMemo(() => {
        if (scaledDiscountedGenericLiabilities.length === 0) {
            return null;
        }

        let total = 0;

        for (const point of scaledDiscountedGenericLiabilities) {
            const absoluteShift = absoluteShiftByDuration[point.time];
            if (typeof absoluteShift === "undefined") {
                return null;
            }

            total -= (point.value * point.time * absoluteShift) / 10000;
        }

        return total;
    }, [absoluteShiftByDuration, scaledDiscountedGenericLiabilities]);

    const relativeIrShiftScaledDiscountedGenericLiability = useMemo(() => {
        if (scaledDiscountedGenericLiabilities.length === 0) {
            return null;
        }

        let total = 0;

        for (const point of scaledDiscountedGenericLiabilities) {
            const relativeShift = relativeShiftByDuration[point.time];
            if (typeof relativeShift === "undefined") {
                return null;
            }

            total -= (point.value * point.time * relativeShift) / 10000;
        }

        return total;
    }, [relativeShiftByDuration, scaledDiscountedGenericLiabilities]);

    const regulatoryCurveChart = useMemo(() => {
        const curvePoints = regulatoryCurve
            .filter((curvePoint) => curvePoint.t >= 1 && curvePoint.t <= 60)
            .sort((a, b) => a.t - b.t)
            .map((curvePoint) => ({ x: curvePoint.t, y: curvePoint.zeroRate }));
        const markers = IRS_INSTRUMENT_NAMES.flatMap((instrumentName) => {
            const series = irPricesData?.timeseries?.find((item) => String(item.instrument?.name ?? "").trim() === instrumentName);
            const priceValue = series?.values?.[0];
            if (typeof priceValue === "undefined") return [];
            const parsedPrice = Number(priceValue);
            if (!Number.isFinite(parsedPrice)) return [];
            return [{ x: IRS_DURATION_BY_INSTRUMENT[instrumentName], y: parsedPrice, name: instrumentName }];
        });
        const bars = scaledDiscountedGenericLiabilities.map((point) => ({ x: point.time, y: point.value }));
        return { curve: curvePoints, markers, bars };
    }, [irPricesData, regulatoryCurve, scaledDiscountedGenericLiabilities]);

    const irInstrumentPriceRows = useMemo(() => {
        const priceByInstrumentName: Record<string, { value: string; date: string }> = {};
        if (irPricesData?.timeseries) {
            irPricesData.timeseries.forEach((series) => {
                const instrumentName = String(series.instrument?.name ?? "").trim();
                const priceDate = series.dates?.[0];
                const priceValue = series.values?.[0];
                if (!instrumentName || !priceDate || typeof priceValue === "undefined") {
                    return;
                }

                priceByInstrumentName[instrumentName] = { value: priceValue, date: priceDate };
            });
        }

        return IRS_INSTRUMENT_NAMES.map((instrumentName) => {
            const price = priceByInstrumentName[instrumentName];
            const relativeShiftParameter = IRS_RELATIVE_SHIFT_BY_INSTRUMENT[instrumentName];
            let priceClose = "";
            let relativeShiftBp = "";
            if (price) {
                const parsedPrice = Number(price.value);
                if (Number.isFinite(parsedPrice)) {
                    priceClose = priceFormat(parsedPrice);
                    relativeShiftBp = twoDecPriceFormat(parsedPrice * relativeShiftParameter * 10000);
                } else {
                    priceClose = price.value;
                }
            }

            return {
                name: instrumentName,
                duration: IRS_DURATION_BY_INSTRUMENT[instrumentName],
                priceDate: price?.date ?? "",
                priceClose,
                absoluteShift: IRS_ABSOLUTE_SHIFT_BY_INSTRUMENT[instrumentName],
                relativeShiftParameter,
                relativeShiftBp
            };
        });
    }, [irPricesData]);

    const totalSharesByReportName = useMemo(() => {
        const totals: Record<string, number> = {};

        latestClientTptReportsByName.forEach((report, reportName) => {
            if (!Array.isArray(report.data)) {
                return;
            }

            const totalShares = getReportTotalShares(report.data as TptRow[], reportName);
            if (totalShares !== null) {
                totals[normalizeIsin(reportName)] = totalShares;
            }
        });

        return totals;
    }, [latestClientTptReportsByName]);

    const sharePriceByReportName = useMemo(() => {
        const sharePrices: Record<string, number> = {};

        latestClientTptReportsByName.forEach((report, reportName) => {
            if (!Array.isArray(report.data)) {
                return;
            }

            const sharePrice = getReportSharePrice(report.data as TptRow[], reportName);
            if (sharePrice !== null) {
                sharePrices[normalizeIsin(reportName)] = sharePrice;
            }
        });

        return sharePrices;
    }, [latestClientTptReportsByName]);

    const reportNameByInstrumentIsin = useMemo(() => {
        const mapping: Record<string, string> = {};

        latestClientTptReportsByName.forEach((report, reportName) => {
            const normalizedReportName = normalizeIsin(reportName);
            mapping[normalizedReportName] = normalizedReportName;

            if (!Array.isArray(report.data)) {
                return;
            }

            (report.data as TptRow[]).forEach((row) => {
                const isin = normalizeIsin(row[TPT_ISIN_COLUMN]);
                if (!isin) {
                    return;
                }

                mapping[isin] = normalizedReportName;
            });
        });

        return mapping;
    }, [latestClientTptReportsByName]);

    const creditStressTotal = useMemo(() => {
        if (!processedData?.allPositions) {
            return 0;
        }

        return processedData.allPositions.reduce((sum, position) => {
            const isin = normalizeIsin(position.isin);
            if (!isin) {
                return sum;
            }

            let baseCreditStress: number | undefined;
            let reportName: string | undefined;
            if (typeof creditStressByReportName[isin] !== "undefined") {
                baseCreditStress = creditStressByReportName[isin];
                reportName = isin;
            } else if (typeof creditStressByIsin[isin] !== "undefined") {
                baseCreditStress = creditStressByIsin[isin];
                reportName = reportNameByInstrumentIsin[isin];
            }

            if (typeof baseCreditStress === "undefined" || !reportName) {
                return sum;
            }

            const scaledCreditStress = scaleStressByPositionQuantityAndSharePrice(
                baseCreditStress,
                position.quantity,
                totalSharesByReportName[reportName] ?? null,
                position.valuationPrice,
                sharePriceByReportName[reportName] ?? null
            );
            if (scaledCreditStress === null) {
                return sum;
            }

            return sum + scaledCreditStress;
        }, 0);
    }, [
        creditStressByIsin,
        creditStressByReportName,
        processedData,
        reportNameByInstrumentIsin,
        sharePriceByReportName,
        totalSharesByReportName
    ]);

    const relativeIrStressTotal = useMemo(() => {
        if (!processedData?.allPositions) {
            return 0;
        }

        return processedData.allPositions.reduce((sum, position) => {
            const isin = normalizeIsin(position.isin);
            if (!isin || typeof relativeIrStressByReportName[isin] === "undefined") {
                return sum;
            }

            const scaledRelativeIrStress = scaleStressByPositionQuantityAndSharePrice(
                relativeIrStressByReportName[isin],
                position.quantity,
                totalSharesByReportName[isin] ?? null,
                position.valuationPrice,
                sharePriceByReportName[isin] ?? null
            );
            if (scaledRelativeIrStress === null) {
                return sum;
            }

            return sum - scaledRelativeIrStress;
        }, 0);
    }, [processedData, relativeIrStressByReportName, sharePriceByReportName, totalSharesByReportName]);

    const absoluteIrStressTotal = useMemo(() => {
        if (!processedData?.allPositions) {
            return 0;
        }

        return processedData.allPositions.reduce((sum, position) => {
            const isin = normalizeIsin(position.isin);
            if (!isin || typeof absoluteIrStressByReportName[isin] === "undefined") {
                return sum;
            }

            const scaledAbsoluteIrStress = scaleStressByPositionQuantityAndSharePrice(
                absoluteIrStressByReportName[isin],
                position.quantity,
                totalSharesByReportName[isin] ?? null,
                position.valuationPrice,
                sharePriceByReportName[isin] ?? null
            );
            if (scaledAbsoluteIrStress === null) {
                return sum;
            }

            return sum - scaledAbsoluteIrStress;
        }, 0);
    }, [absoluteIrStressByReportName, processedData, sharePriceByReportName, totalSharesByReportName]);

    const equityStressTotal = useMemo(() => {
        if (!processedData?.allPositions) {
            return 0;
        }

        return processedData.allPositions.reduce((sum, position) => {
            const isin = normalizeIsin(position.isin);
            if (!isin || typeof equityStressByReportName[isin] === "undefined") {
                return sum;
            }

            const scaledEquityStress = scaleStressByPositionQuantityAndSharePrice(
                equityStressByReportName[isin],
                position.quantity,
                totalSharesByReportName[isin] ?? null,
                position.valuationPrice,
                sharePriceByReportName[isin] ?? null
            );
            if (scaledEquityStress === null) {
                return sum;
            }

            return sum + scaledEquityStress;
        }, 0);
    }, [equityStressByReportName, processedData, sharePriceByReportName, totalSharesByReportName]);

    const fxStressTotal = useMemo(() => {
        if (!processedData?.allPositions) {
            return 0;
        }

        return processedData.allPositions.reduce((sum, position) => {
            const isin = normalizeIsin(position.isin);
            if (!isin || typeof fxStressByReportName[isin] === "undefined") {
                return sum;
            }

            const scaledFxStress = scaleStressByPositionQuantityAndSharePrice(
                fxStressByReportName[isin],
                position.quantity,
                totalSharesByReportName[isin] ?? null,
                position.valuationPrice,
                sharePriceByReportName[isin] ?? null
            );
            if (scaledFxStress === null) {
                return sum;
            }

            return sum + scaledFxStress;
        }, 0);
    }, [fxStressByReportName, processedData, sharePriceByReportName, totalSharesByReportName]);

    const tptReportDateByIsin = useMemo(() => {
        const reportDatesByIsin: Record<string, string> = {};

        latestClientTptReportsByName.forEach((report) => {
            const reportName = String(report.name ?? "").trim();
            if (!reportName) {
                return;
            }

            const normalizedReportName = normalizeIsin(reportName);
            if (!reportDatesByIsin[normalizedReportName]) {
                reportDatesByIsin[normalizedReportName] = report.date;
            }
            if (!reportDatesByIsin[reportName]) {
                reportDatesByIsin[reportName] = report.date;
            }

            if (!Array.isArray(report.data)) {
                return;
            }

            (report.data as TptRow[]).forEach((row) => {
                const isin = normalizeIsin(row[TPT_ISIN_COLUMN]);
                if (!isin || reportDatesByIsin[isin]) {
                    return;
                }

                const rowDate = String(row["6_Valuation_date"] ?? "").trim();
                reportDatesByIsin[isin] = rowDate || report.date;
            });
        });

        return reportDatesByIsin;
    }, [latestClientTptReportsByName]);

    const positionsWithTptReportDate = useMemo(() => {
        if (!processedData) {
            return null;
        }

        if (!showTptReportDate) {
            return processedData;
        }

        return {
            ...processedData,
            allPositions: processedData.allPositions.map((position) => {
                const isin = normalizeIsin(position.isin);
                let creditStress: number | null = null;
                let relativeIrStress: number | null = null;
                let absoluteIrStress: number | null = null;
                let equityStress: number | null = null;
                let fxStress: number | null = null;
                if (isin) {
                    let baseCreditStress: number | undefined;
                    let reportName: string | undefined;
                    if (typeof creditStressByReportName[isin] !== "undefined") {
                        baseCreditStress = creditStressByReportName[isin];
                        reportName = isin;
                    } else if (typeof creditStressByIsin[isin] !== "undefined") {
                        baseCreditStress = creditStressByIsin[isin];
                        reportName = reportNameByInstrumentIsin[isin];
                    }

                    if (typeof baseCreditStress !== "undefined" && reportName) {
                        creditStress = scaleStressByPositionQuantityAndSharePrice(
                            baseCreditStress,
                            position.quantity,
                            totalSharesByReportName[reportName] ?? null,
                            position.valuationPrice,
                            sharePriceByReportName[reportName] ?? null
                        );
                    }

                    if (typeof relativeIrStressByReportName[isin] !== "undefined") {
                        relativeIrStress = scaleStressByPositionQuantityAndSharePrice(
                            relativeIrStressByReportName[isin],
                            position.quantity,
                            totalSharesByReportName[isin] ?? null,
                            position.valuationPrice,
                            sharePriceByReportName[isin] ?? null
                        );
                        if (relativeIrStress !== null) {
                            relativeIrStress *= -1;
                        }
                    }

                    if (typeof absoluteIrStressByReportName[isin] !== "undefined") {
                        absoluteIrStress = scaleStressByPositionQuantityAndSharePrice(
                            absoluteIrStressByReportName[isin],
                            position.quantity,
                            totalSharesByReportName[isin] ?? null,
                            position.valuationPrice,
                            sharePriceByReportName[isin] ?? null
                        );
                        if (absoluteIrStress !== null) {
                            absoluteIrStress *= -1;
                        }
                    }

                    if (typeof equityStressByReportName[isin] !== "undefined") {
                        equityStress = scaleStressByPositionQuantityAndSharePrice(
                            equityStressByReportName[isin],
                            position.quantity,
                            totalSharesByReportName[isin] ?? null,
                            position.valuationPrice,
                            sharePriceByReportName[isin] ?? null
                        );
                    }

                    if (typeof fxStressByReportName[isin] !== "undefined") {
                        fxStress = scaleStressByPositionQuantityAndSharePrice(
                            fxStressByReportName[isin],
                            position.quantity,
                            totalSharesByReportName[isin] ?? null,
                            position.valuationPrice,
                            sharePriceByReportName[isin] ?? null
                        );
                    }
                }
                return {
                    ...position,
                    tptReportDate: isin ? (tptReportDateByIsin[isin] ?? tptReportDateByIsin[position.isin] ?? null) : null,
                    creditStress,
                    relativeIrStress,
                    absoluteIrStress,
                    equityStress,
                    fxStress
                };
            })
        };
    }, [
        absoluteIrStressByReportName,
        creditStressByIsin,
        creditStressByReportName,
        equityStressByReportName,
        fxStressByReportName,
        processedData,
        relativeIrStressByReportName,
        reportNameByInstrumentIsin,
        sharePriceByReportName,
        showTptReportDate,
        totalSharesByReportName,
        tptReportDateByIsin
    ]);

    const positionsTopNodeTotals = useMemo(() => {
        let exposure = processedData?.portfolioValue ?? 0;
        if (scaledDiscountedGenericLiability !== null) {
            exposure -= scaledDiscountedGenericLiability;
        }

        let relativeIrStress = relativeIrStressTotal;
        if (relativeIrShiftScaledDiscountedGenericLiability !== null) {
            relativeIrStress -= relativeIrShiftScaledDiscountedGenericLiability;
        }

        let absoluteIrStress = absoluteIrStressTotal;
        if (absoluteIrShiftScaledDiscountedGenericLiability !== null) {
            absoluteIrStress -= absoluteIrShiftScaledDiscountedGenericLiability;
        }

        const totalStress = calculateCorrelatedTotalStress(
            [Math.max(relativeIrStress, absoluteIrStress), equityStressTotal, 0, creditStressTotal, fxStressTotal, 0, 0],
            REGULATORY_STRESS_CORRELATION_MATRIX
        );

        return {
            exposure,
            relativeIrStress,
            absoluteIrStress,
            creditStress: creditStressTotal,
            equityStress: equityStressTotal,
            fxStress: fxStressTotal,
            totalStress
        };
    }, [
        absoluteIrShiftScaledDiscountedGenericLiability,
        absoluteIrStressTotal,
        creditStressTotal,
        equityStressTotal,
        fxStressTotal,
        processedData?.portfolioValue,
        relativeIrShiftScaledDiscountedGenericLiability,
        relativeIrStressTotal,
        scaledDiscountedGenericLiability
    ]);

    useEffect(() => {
        onPositionsTopNodeTotalsChange?.({
            exposure: positionsTopNodeTotals.exposure,
            totalStress: positionsTopNodeTotals.totalStress
        });
    }, [positionsTopNodeTotals, onPositionsTopNodeTotalsChange]);

    const positionsTableData = useMemo(() => {
        const allPositions = positionsWithTptReportDate?.allPositions ?? processedData?.allPositions;
        if (!allPositions) {
            return [];
        }

        const liabilityRow = {
            isLiabilityRow: true,
            positionsRoot: POSITIONS_ROOT_NAME,
            clientName: "Liability",
            modelType: "Liability",
            name: "Liability",
            longName: "",
            isin: "",
            exposure: scaledDiscountedGenericLiability !== null ? -scaledDiscountedGenericLiability : null,
            relativeIrStress:
                relativeIrShiftScaledDiscountedGenericLiability !== null ? -relativeIrShiftScaledDiscountedGenericLiability : null,
            absoluteIrStress:
                absoluteIrShiftScaledDiscountedGenericLiability !== null ? -absoluteIrShiftScaledDiscountedGenericLiability : null,
            creditStress: null,
            equityStress: null,
            fxStress: null,
            tptReportDate: null,
            valuationPrice: null,
            quantity: null,
            currency: "",
            valuationDate: "",
            isCashAccount: false
        };

        return [liabilityRow, ...allPositions.map((position) => ({ ...position, positionsRoot: POSITIONS_ROOT_NAME }))];
    }, [
        absoluteIrShiftScaledDiscountedGenericLiability,
        positionsWithTptReportDate,
        processedData,
        relativeIrShiftScaledDiscountedGenericLiability,
        scaledDiscountedGenericLiability
    ]);

    const columns = React.useMemo(
        () => [
            {
                header: "Root",
                accessorKey: "positionsRoot"
            },
            {
                header: "Client",
                accessorKey: "clientName"
            },
            {
                header: "Type",
                accessorKey: "modelType"
            },
            {
                header: "Name",
                accessorKey: "name",
                cell: (cellProps) => {
                    if (cellProps.row.original.isLiabilityRow) {
                        return "Liability";
                    }

                    const { row } = cellProps;
                    if (row.original.isCashAccount) {
                        return (
                            <a
                                href={`/parties/${selectedClient._id}/instruments/${row.original.instrumentId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {cellProps.getValue()}
                            </a>
                        );
                    }

                    return (
                        <a href={`/instruments/${row.original.instrumentId}`} target="_blank" rel="noopener noreferrer">
                            {cellProps.getValue()}
                        </a>
                    );
                }
            },
            {
                header: "Long name",
                accessorKey: "longName"
            },
            {
                header: "ISIN",
                accessorKey: "isin",
                size: 90
            },
            ...(showTptReportDate
                ? [
                      {
                          header: "TPT report date",
                          accessorKey: "tptReportDate",
                          cell: (cellProps) => cellProps.getValue() ?? "-",
                          aggregatedCell: () => null,
                          size: 110
                      },
                      {
                          header: "Credit stress",
                          accessorKey: "creditStress",
                          cell: (cellProps) => {
                              if (cellProps.row.subRows?.length) {
                                  return null;
                              }

                              const value = cellProps.getValue();
                              return (
                                  <div style={{ textAlign: "right" }}>
                                      {value === null || typeof value === "undefined" ? "-" : twoDecPriceFormat(value)}
                                  </div>
                              );
                          },
                          aggregatedCell: (cellProps) => {
                              if (cellProps.row.depth === 0) {
                                  return renderPositionsTopNodeAggregatedValue(positionsTopNodeTotals.creditStress);
                              }

                              if (getLiabilityLeafOriginal(cellProps.row)) {
                                  return <div style={{ textAlign: "right" }}>-</div>;
                              }

                              if (cellProps.row.depth !== 1) {
                                  return null;
                              }

                              return <div style={{ textAlign: "right" }}>{twoDecPriceFormat(creditStressTotal)}</div>;
                          },
                          size: 110
                      },
                      {
                          header: "Relative IR stress",
                          accessorKey: "relativeIrStress",
                          cell: (cellProps) => {
                              if (cellProps.row.subRows?.length) {
                                  return null;
                              }

                              const value = cellProps.getValue();
                              return (
                                  <div style={{ textAlign: "right" }}>
                                      {value === null || typeof value === "undefined" ? "-" : twoDecPriceFormat(value)}
                                  </div>
                              );
                          },
                          aggregatedCell: (cellProps) => {
                              if (cellProps.row.depth === 0) {
                                  return renderPositionsTopNodeAggregatedValue(positionsTopNodeTotals.relativeIrStress);
                              }

                              const liabilityValue = renderLiabilityAggregatedValue(cellProps.row, "relativeIrStress");
                              if (liabilityValue) {
                                  return liabilityValue;
                              }

                              if (cellProps.row.depth !== 1) {
                                  return null;
                              }

                              return <div style={{ textAlign: "right" }}>{twoDecPriceFormat(relativeIrStressTotal)}</div>;
                          },
                          size: 110
                      },
                      {
                          header: "Absolute IR stress",
                          accessorKey: "absoluteIrStress",
                          cell: (cellProps) => {
                              if (cellProps.row.subRows?.length) {
                                  return null;
                              }

                              const value = cellProps.getValue();
                              return (
                                  <div style={{ textAlign: "right" }}>
                                      {value === null || typeof value === "undefined" ? "-" : twoDecPriceFormat(value)}
                                  </div>
                              );
                          },
                          aggregatedCell: (cellProps) => {
                              if (cellProps.row.depth === 0) {
                                  return renderPositionsTopNodeAggregatedValue(positionsTopNodeTotals.absoluteIrStress);
                              }

                              const liabilityValue = renderLiabilityAggregatedValue(cellProps.row, "absoluteIrStress");
                              if (liabilityValue) {
                                  return liabilityValue;
                              }

                              if (cellProps.row.depth !== 1) {
                                  return null;
                              }

                              return <div style={{ textAlign: "right" }}>{twoDecPriceFormat(absoluteIrStressTotal)}</div>;
                          },
                          size: 110
                      },
                      {
                          header: "Equity stress",
                          accessorKey: "equityStress",
                          cell: (cellProps) => {
                              if (cellProps.row.subRows?.length) {
                                  return null;
                              }

                              const value = cellProps.getValue();
                              return (
                                  <div style={{ textAlign: "right" }}>
                                      {value === null || typeof value === "undefined" ? "-" : twoDecPriceFormat(value)}
                                  </div>
                              );
                          },
                          aggregatedCell: (cellProps) => {
                              if (cellProps.row.depth === 0) {
                                  return renderPositionsTopNodeAggregatedValue(positionsTopNodeTotals.equityStress);
                              }

                              if (getLiabilityLeafOriginal(cellProps.row)) {
                                  return <div style={{ textAlign: "right" }}>-</div>;
                              }

                              if (cellProps.row.depth !== 1) {
                                  return null;
                              }

                              return <div style={{ textAlign: "right" }}>{twoDecPriceFormat(equityStressTotal)}</div>;
                          },
                          size: 110
                      },
                      {
                          header: "FX stress",
                          accessorKey: "fxStress",
                          cell: (cellProps) => {
                              if (cellProps.row.subRows?.length) {
                                  return null;
                              }

                              const value = cellProps.getValue();
                              return (
                                  <div style={{ textAlign: "right" }}>
                                      {value === null || typeof value === "undefined" ? "-" : twoDecPriceFormat(value)}
                                  </div>
                              );
                          },
                          aggregatedCell: (cellProps) => {
                              if (cellProps.row.depth === 0) {
                                  return renderPositionsTopNodeAggregatedValue(positionsTopNodeTotals.fxStress);
                              }

                              if (getLiabilityLeafOriginal(cellProps.row)) {
                                  return <div style={{ textAlign: "right" }}>-</div>;
                              }

                              if (cellProps.row.depth !== 1) {
                                  return null;
                              }

                              return <div style={{ textAlign: "right" }}>{twoDecPriceFormat(fxStressTotal)}</div>;
                          },
                          size: 110
                      },
                      {
                          header: "Total stress",
                          id: "totalStress",
                          cell: () => null,
                          aggregatedCell: (cellProps) => {
                              if (cellProps.row.depth !== 0) {
                                  return null;
                              }

                              return renderPositionsTopNodeTotalStressCell(
                                  positionsTopNodeTotals.totalStress,
                                  positionsTopNodeTotals.exposure
                              );
                          },
                          size: 110
                      }
                  ]
                : []),
            {
                header: "Price",
                accessorKey: "valuationPrice",
                cell: (cellProps) => <div style={{ textAlign: "right" }}>{priceFormat(cellProps.getValue())}</div>,
                aggregatedCell: () => null,
                size: 90
            },
            {
                header: "Quantity",
                accessorKey: "quantity",
                cell: (cellProps) => <div style={{ textAlign: "right" }}>{twoDecPriceFormat(cellProps.getValue())}</div>,
                aggregatedCell: () => null
            },
            {
                header: "Value (SEK)",
                accessorKey: "exposure",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return (
                        <div style={{ textAlign: "right" }}>
                            {value === null || typeof value === "undefined" ? "-" : twoDecPriceFormat(value)}
                        </div>
                    );
                },
                aggregationFn: "sum",
                aggregatedCell: (cellProps) => {
                    if (cellProps.row.depth === 0) {
                        return renderPositionsTopNodeAggregatedValue(positionsTopNodeTotals.exposure);
                    }

                    const liabilityValue = renderLiabilityAggregatedValue(cellProps.row, "exposure");
                    if (liabilityValue) {
                        return liabilityValue;
                    }

                    return <div style={{ textAlign: "right" }}>{twoDecPriceFormat(cellProps.getValue())}</div>;
                }
            },
            {
                header: "Currency",
                accessorKey: "currency",
                size: 65
            },
            {
                header: "Price date",
                accessorKey: "valuationDate",
                size: 90
            }
        ],
        [
            absoluteIrStressTotal,
            creditStressTotal,
            equityStressTotal,
            fxStressTotal,
            relativeIrStressTotal,
            positionsTopNodeTotals,
            selectedClient,
            showTptReportDate
        ]
    );

    useEffect(() => {
        if (
            date &&
            !queryArgs.get("date") &&
            tabId &&
            tabId === "positions" &&
            (!previousClientDashName || (previousClientDashName && previousTabId !== tabId))
        ) {
            setQueryArgs((prev) => {
                prev.set("date", date);
                return prev;
            });
        }
        if (dataClients) {
            if (!showClientSelector) {
                if (contextClient._id === emptyObjectId) {
                    if (selectedClient) {
                        setSelectedClient(null);
                    }
                    return;
                }

                const client = dataClients.clients.find((party) => party._id === contextClient._id);
                if (!client) {
                    if (selectedClient) {
                        setSelectedClient(null);
                    }
                    return;
                }

                if (!selectedClient || selectedClient._id !== client._id) {
                    setSelectedClient({
                        value: client._id,
                        label: client.name,
                        name: client.name,
                        _id: client._id,
                        instruments: client.instruments,
                        accountingCurrency: client.accountingCurrency
                    });
                }

                if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
                    const filter: TransactionFilterInput = { endDate: date, clientIds: [client._id], accountIds: null };
                    setFilter(filter);
                }

                return;
            }

            const dashNameToClient: Record<string, any> = {};
            dataClients.clients.forEach((client) => {
                const dashName = client.name.replace(/ /g, "-");
                dashNameToClient[dashName] = client;
            });

            if (!clientDashName && selectedClient) {
                setSelectedClient(null);
            } else if (!selectedClient || clientDashName !== previousClientDashName) {
                const client = dashNameToClient[clientDashName];
                if (client) {
                    setSelectedClient({
                        value: client._id,
                        label: client.name,
                        name: client.name,
                        _id: client._id,
                        instruments: client.instruments,
                        accountingCurrency: client.accountingCurrency
                    });
                    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
                        const filter: TransactionFilterInput = { endDate: date, clientIds: [client._id], accountIds: null };
                        setFilter(filter);
                    }
                } else {
                    const [pathname] = location.split("?");
                    if (pathname !== "/positions" && !tabId) {
                        navigate("/positions");
                    }
                }
            }

            if (
                selectedClient &&
                (selectedClient !== previousSelectedClient || date !== previousDate) &&
                /^\d{4}-\d{2}-\d{2}$/.test(date)
            ) {
                const filter: TransactionFilterInput = { endDate: date, clientIds: [selectedClient._id], accountIds: null };
                setFilter(filter);
            }
        }
    }, [
        clientDashName,
        contextClient._id,
        dataClients,
        date,
        location,
        navigate,
        previousClientDashName,
        previousDate,
        previousSelectedClient,
        previousTabId,
        queryArgs,
        selectedClient,
        setQueryArgs,
        showClientSelector,
        tabId
    ]);

    useEffect(() => {
        if (selectedClient && dataClients && dataClients.clients) {
            const client = dataClients.clients.find((x) => x._id == selectedClient._id);
            document.title = `IORP 2 - ${client?.name}`;
        }
    }, [selectedClient, dataClients]);

    if (showClientSelector) {
        if (error) {
            return (
                <div>
                    <p>error:</p>
                    <pre> {JSON.stringify(error, null, 2)}</pre>
                </div>
            );
        }

        if (loadingClients) {
            return <p>Loading clients</p>;
        }

        if (errorClients) {
            return <p>errorClients: {JSON.stringify(errorClients, null, 2)}</p>;
        }

        if (loading) {
            return <p>Loading</p>;
        }
    }

    let options = [];

    if (showClientSelector) {
        dataClients.clients.forEach((client) => {
            if (client.instruments && client.instruments.length > 0) {
                options.push({ value: client._id, label: client.name, _id: client._id, instruments: client.instruments });
            }
        });
        options = sortBy(options, "label");
    }

    const expandedPositions = selectedClient
        ? {
              [`positionsRoot:${POSITIONS_ROOT_NAME}`]: true,
              [`positionsRoot:${POSITIONS_ROOT_NAME}>clientName:Liability`]: true,
              [`positionsRoot:${POSITIONS_ROOT_NAME}>clientName:${selectedClient.label}`]: true
          }
        : {};
    const showPositionsDate = showClientSelector ? !!processedData : contextClient._id !== emptyObjectId;
    const positionsData = positionsWithTptReportDate ?? processedData;
    const showPositionsContent = !!positionsData;

    return (
        <div>
            <Page className="positionspage">
                <div>
                    {showClientSelector ? (
                        <Fragment>
                            <h4>Client</h4>
                            <div className="flex flex-wrap -mx-2 mb-2">
                                <div className="w-full md:w-4/12 px-2 mb-4">
                                    <Dropdown>
                                        <Dropdown.Toggle variant="light" id={"selectClient"}>
                                            {selectedClient ? selectedClient.label : "Select client"}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            {options.map((option) => (
                                                <Dropdown.Item
                                                    key={option._id}
                                                    onClick={(e) => {
                                                        console.log("Selected client:", option, e);
                                                        const dashName = option.label.replace(/ /g, "-");
                                                        navigate("/positions/" + dashName);
                                                    }}
                                                >
                                                    {option.label}
                                                </Dropdown.Item>
                                            ))}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </div>
                            </div>
                        </Fragment>
                    ) : null}
                    {showPositionsDate ? (
                        <div className="flex flex-wrap -mx-2 flex">
                            <div className="w-full md:w-4/12 px-2 mb-4">
                                <DateForm defaultDateString={date} dateName={"date"}></DateForm>
                            </div>
                        </div>
                    ) : null}
                    {!showClientSelector && loadingClients ? <p>Loading clients</p> : null}
                    {!showClientSelector && errorClients ? <p>errorClients: {JSON.stringify(errorClients, null, 2)}</p> : null}
                    {!showClientSelector && error ? (
                        <div>
                            <p>error:</p>
                            <pre> {JSON.stringify(error, null, 2)}</pre>
                        </div>
                    ) : null}
                    {!showClientSelector && loading ? <p>Loading</p> : null}
                    {showPositionsContent ? (
                        <Fragment>
                            <div
                                style={{ width: "20px", height: "20px", marginBottom: "1.0rem", marginTop: "1.0rem" }}
                                onClick={() => {
                                    if (positionsData && positionsData.allPositions && positionsData.allPositions.length) {
                                        const currentRecords: sheetType = positionsData.allPositions.map((i) => {
                                            delete i.__typename;
                                            return i;
                                        });
                                        exportToXlsx([currentRecords], "positions.xlsx");
                                    }
                                }}
                            >
                                <FileSpreadsheet className="Excel" />
                            </div>

                            <div className="inline-flex flex-wrap mt-2">
                                <div className="mr-3">
                                    <h4>Positions</h4>
                                    <TableGrouper
                                        columns={columns}
                                        data={positionsTableData}
                                        expanded={expandedPositions}
                                        groupBy={["positionsRoot", "clientName", "modelType"]}
                                        hiddenColumns={["positionsRoot"]}
                                        compactMode={true}
                                        captionColumn="name"
                                    />
                                </div>
                            </div>
                        </Fragment>
                    ) : null}
                    {showPositionsDate || uploadPanel ? (
                        <div className="mt-4 flex flex-wrap -mx-2 items-start gap-y-4">
                            {showPositionsDate ? (
                                <div className="w-full md:w-5/12 lg:w-7/12 xl:w-8/12 px-2 min-w-0 overflow-x-auto">
                                    <h4>IRS instruments</h4>
                                    {loadingIrPrices ? <p>Loading IRS prices</p> : null}
                                    {errorIrPrices ? <p>error: {JSON.stringify(errorIrPrices, null, 2)}</p> : null}
                                    {!loadingIrPrices && !errorIrPrices ? (
                                        <Fragment>
                                            {regulatoryCurveChart.curve.length > 0 ||
                                            regulatoryCurveChart.markers.length > 0 ||
                                            regulatoryCurveChart.bars.length > 0 ? (
                                                <div className="mb-4 w-3/4 min-w-0">
                                                    <DurationCurveChart
                                                        curve={regulatoryCurveChart.curve}
                                                        markers={regulatoryCurveChart.markers}
                                                        bars={regulatoryCurveChart.bars}
                                                        ariaLabel="Regulatory curve"
                                                        y2Label={regulatoryCurveChart.bars.length > 0 ? "Scaled discounted generic liability" : undefined}
                                                        height={360}
                                                        style={{ width: "100%", height: "360px" }}
                                                    />
                                                </div>
                                            ) : null}
                                            {discountedGenericLiabilityTotal !== null ? (
                                                <div className="mb-4 flex flex-wrap items-center gap-4">
                                                    <span>
                                                        Discounted generic liability: {twoDecPriceFormat(discountedGenericLiabilityTotal)}
                                                    </span>
                                                    <label className="flex items-center gap-2">
                                                        <span>Solvency</span>
                                                        <input
                                                            type="number"
                                                            step="any"
                                                            min="0"
                                                            value={solvency}
                                                            onChange={(event) => setSolvency(event.target.value)}
                                                        />
                                                    </label>
                                                    {scaledDiscountedGenericLiability !== null ? (
                                                        <span>
                                                            Scaled discounted generic liability:{" "}
                                                            {twoDecPriceFormat(scaledDiscountedGenericLiability)}
                                                        </span>
                                                    ) : null}
                                                    {absoluteIrShiftScaledDiscountedGenericLiability !== null ? (
                                                        <span>
                                                            Absolute IR shift on scaled discounted generic liability:{" "}
                                                            {twoDecPriceFormat(absoluteIrShiftScaledDiscountedGenericLiability)}
                                                        </span>
                                                    ) : null}
                                                    {relativeIrShiftScaledDiscountedGenericLiability !== null ? (
                                                        <span>
                                                            Relative IR shift on scaled discounted generic liability:{" "}
                                                            {twoDecPriceFormat(relativeIrShiftScaledDiscountedGenericLiability)}
                                                        </span>
                                                    ) : null}
                                                </div>
                                            ) : null}
                                            <Grid
                                                data={irInstrumentPriceRows}
                                                className="table-timeseries"
                                                tableClassName="table-xs"
                                                sortable
                                            >
                                                <Column field="name" title="Instrument" className="nowrap left" />
                                                <Column field="duration" title="Duration" className="nowrap left" />
                                                <Column field="priceDate" title="Date" className="nowrap left" />
                                                <Column field="priceClose" title="Price(Close)" className="nowrap left" />
                                                <Column field="absoluteShift" title="Absolute shift" className="nowrap left" />
                                                <Column
                                                    field="relativeShiftParameter"
                                                    title="Relative shift parameter"
                                                    className="nowrap left"
                                                />
                                                <Column field="relativeShiftBp" title="Relative shift (bp)" className="nowrap left" />
                                            </Grid>
                                        </Fragment>
                                    ) : null}
                                </div>
                            ) : null}
                            {uploadPanel ? <div className="w-full md:w-7/12 lg:w-5/12 xl:w-4/12 px-2 min-w-0">{uploadPanel}</div> : null}
                        </div>
                    ) : null}
                </div>
            </Page>
        </div>
    );
}

export function Iorp2Page(): React.ReactElement {
    const { client } = useContext(ClientContext);
    const gqlClient = useClient();
    const [topNodeTotalsForRkk, setTopNodeTotalsForRkk] = useState<PositionsTopNodeTotalsSnapshot | null>(null);
    const handleTopNodeTotalsChange = useCallback((totals: PositionsTopNodeTotalsSnapshot) => {
        setTopNodeTotalsForRkk(totals);
    }, []);
    const [fileName, setFileName] = useState<string>("");
    const [rows, setRows] = useState<TptRow[]>([]);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [saveMessage, setSaveMessage] = useState<string>("");
    const [saveReportModal, setSaveReportModal] = useState<{
        showModal: boolean;
        payload: CreateReportInput | null;
        latestSavedDate: string | null;
    }>({
        showModal: false,
        payload: null,
        latestSavedDate: null
    });
    const [tptReportRefreshKey, setTptReportRefreshKey] = useState(0);
    const [{ fetching: savingReport }, createReport] = useMutation(CREATE_TPT_REPORT);

    const rkkQuota = useMemo(() => {
        if (!topNodeTotalsForRkk) {
            return null;
        }
        const { exposure, totalStress } = topNodeTotalsForRkk;
        if (!Number.isFinite(exposure) || !Number.isFinite(totalStress) || totalStress === 0) {
            return null;
        }
        return exposure / totalStress;
    }, [topNodeTotalsForRkk]);

    const rkkQuotaValueClassName = useMemo(() => {
        if (rkkQuota === null) {
            return "text-gray-900";
        }
        if (rkkQuota >= 1.2) {
            return "text-green-600";
        }
        if (rkkQuota > 1 && rkkQuota < 1.2) {
            return "text-yellow-600";
        }
        return "text-red-600";
    }, [rkkQuota]);

    const jsonOutput = useMemo(() => JSON.stringify(rows, null, 2), [rows]);
    const { creditStressTotal } = useMemo(() => buildTptCreditStressByIsin(rows), [rows]);
    const marketExposureByCreditRating = useMemo(() => {
        const groupedSums = new Map<string, { sum: number; stressedSum: number }>();

        rows.forEach((row) => {
            const coveredFlag = String(row["55_Covered_not_covered"] ?? "").trim();
            const creditRating = coveredFlag === "C" ? "Coverd" : String(row["112_Credit_rating"] ?? "").trim() || "Unrated";
            const amount = parseNumericCell(row["24_Market_valuation_in_portfolio_currency_(B)"]);

            if (amount === null) {
                return;
            }

            const stressedAmount = getTptRowStressedAmount(row);
            const current = groupedSums.get(creditRating) ?? { sum: 0, stressedSum: 0 };
            groupedSums.set(creditRating, { sum: current.sum + amount, stressedSum: current.stressedSum + stressedAmount });
        });

        return Array.from(groupedSums.entries())
            .map(([creditRating, value]): ExposureSummaryRow => {
                let weightedStressPercent: number | null = null;
                if (value.sum !== 0) {
                    weightedStressPercent = (value.stressedSum / value.sum) * 100;
                }
                return {
                    creditRating,
                    sum: value.sum,
                    stressedSum: value.stressedSum,
                    weightedStressPercent
                };
            })
            .sort((a, b) => b.sum - a.sum);
    }, [rows]);
    const marketExposureTotal = useMemo(
        () => marketExposureByCreditRating.reduce((acc, row) => acc + row.sum, 0),
        [marketExposureByCreditRating]
    );
    const stressedTotal = creditStressTotal;

    const onDrop = useCallback((acceptedFiles: File[]) => {
        const file = acceptedFiles[0];
        if (!file) {
            return;
        }

        setErrorMessage("");
        setSaveMessage("");
        setFileName(file.name);

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const arrayBuffer = event.target?.result;
                if (!arrayBuffer) {
                    setRows([]);
                    setErrorMessage("Could not read file content.");
                    return;
                }

                const workbook = XLSX.read(new Uint8Array(arrayBuffer as ArrayBuffer), { type: "array" });
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];
                const matrixData = XLSX.utils.sheet_to_json<(string | number | boolean | null)[]>(worksheet, {
                    header: 1,
                    raw: false,
                    defval: null
                });

                if (!matrixData.length) {
                    setRows([]);
                    setErrorMessage("The Excel file is empty.");
                    return;
                }

                const rawHeaders = matrixData[0];
                const headers = rawHeaders.map((header, index) => {
                    const headerText = String(header ?? "").trim();
                    return headerText || `column_${index + 1}`;
                });

                const parsedRows = matrixData
                    .slice(1)
                    .filter((row) => row.some((cell) => String(cell ?? "").trim() !== ""))
                    .map((row) => {
                        const objectRow: TptRow = {};
                        headers.forEach((header, index) => {
                            objectRow[header] = typeof row[index] === "undefined" ? null : row[index];
                        });
                        return objectRow;
                    });

                setRows(parsedRows);
            } catch {
                setRows([]);
                setErrorMessage("Failed to parse Excel file. Upload a valid .xlsx or .xls file.");
            }
        };

        reader.readAsArrayBuffer(file);
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
            "application/vnd.ms-excel": [".xls"]
        },
        multiple: false
    });

    const handleDownloadJson = () => {
        const blob = new Blob([jsonOutput], { type: "application/json;charset=utf-8" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = fileName ? `${getTptReportName(fileName)}.json` : "tpt.json";
        link.click();
        URL.revokeObjectURL(link.href);
    };

    const saveTptReport = async (input: CreateReportInput) => {
        const result = await createReport({ input });
        if (result.error) {
            setSaveMessage(formikUrqlErrorFormater(result.error));
            return;
        }

        setSaveMessage(`Saved TPT report "${result.data.report.name}" for ${client.name}.`);
        setTptReportRefreshKey((current) => current + 1);
    };

    const handleSaveReport = async () => {
        setSaveMessage("");

        if (!rows.length) {
            setSaveMessage("Upload and parse an Excel file before saving a report.");
            return;
        }

        if (client._id === emptyObjectId) {
            setSaveMessage("Select a client before saving a report.");
            return;
        }

        const reportMetadata = getTptReportMetadata(rows);
        if (!reportMetadata) {
            setSaveMessage(`Could not read "${TPT_PORTFOLIO_ID_COLUMN}" or "${TPT_VALUATION_DATE_COLUMN}" from the uploaded file.`);
            return;
        }

        const input: CreateReportInput = {
            clientId: client._id,
            type: "TPT",
            name: reportMetadata.name,
            date: reportMetadata.date,
            data: rows,
            status: ReportStatusEnum.Active
        };

        const existingReportsResult = await gqlClient
            .query(GET_TPT_REPORTS_BY_NAME, { clientId: client._id, nameIn: [reportMetadata.name] }, { requestPolicy: "network-only" })
            .toPromise();
        if (existingReportsResult.error) {
            setSaveMessage(formikUrqlErrorFormater(existingReportsResult.error));
            return;
        }

        const latestSavedDate = existingReportsResult.data?.reports?.[0]?.date;
        if (!isTptReportDateLaterThanLatest(reportMetadata.date, latestSavedDate)) {
            setSaveReportModal({ showModal: true, payload: input, latestSavedDate: latestSavedDate ?? null });
            return;
        }

        await saveTptReport(input);
    };

    return (
        <div className="p-4">
            {saveReportModal.showModal && saveReportModal.payload ? (
                <YesNoModal
                    warningText={`The latest saved TPT report for "${saveReportModal.payload.name}" is dated ${saveReportModal.latestSavedDate ?? "unknown"}. The new report date is ${saveReportModal.payload.date}, which is not later than the latest saved report. Save anyway?`}
                    yesText="Save report"
                    noText="Cancel"
                    modal={saveReportModal}
                    setModal={({ showModal, payload }) => {
                        setSaveReportModal({
                            showModal,
                            payload: payload as CreateReportInput | null,
                            latestSavedDate: showModal ? saveReportModal.latestSavedDate : null
                        });
                    }}
                    onYes={() => {
                        if (saveReportModal.payload) {
                            void saveTptReport(saveReportModal.payload);
                        }
                    }}
                />
            ) : null}
            <h1 className="mb-4">IORP 2</h1>
            <div className="flex flex-wrap items-end justify-between gap-y-4 -mx-2 print-none">
                <div className="w-full sm:w-1/3 px-2 mb-4 min-w-0">
                    <ClientContextSelector
                        typeIn={[PartyType.Client]}
                        carryQueryArgs={false}
                        statusIn={[PartyStatusEnum.Confirmed, PartyStatusEnum.Inactive]}
                    />
                </div>
                <div className="w-full sm:w-auto px-2 mb-4 sm:text-right shrink-0 pr-[25vw]">
                    <div className="text-3xl font-semibold tabular-nums sm:inline-block">
                        <span className="text-gray-800">RKK Quota: </span>
                        <strong className={rkkQuotaValueClassName}>{rkkQuota === null ? "—" : twoDecPriceFormat(rkkQuota)}</strong>
                    </div>
                </div>
            </div>

            <PositionsGroupedPage
                showClientSelector={false}
                showTptReportDate={true}
                tptReportRefreshKey={tptReportRefreshKey}
                onPositionsTopNodeTotalsChange={handleTopNodeTotalsChange}
                uploadPanel={
                    <div className="min-w-0">
                        <div
                            {...getRootProps()}
                            className={`min-h-[12rem] rounded border-2 border-dashed p-6 text-center transition-colors ${
                                isDragActive ? "border-sky-500 bg-sky-50" : "border-gray-300 bg-gray-50"
                            }`}
                        >
                            <input {...getInputProps()} />
                        </div>
                        <p className="mt-3 text-center">
                            {isDragActive ? "Drop file here..." : "Drag & drop .xlsx/.xls, or click to select file"}
                        </p>
                        <p className="mt-3 text-sm text-gray-600">Drop an Excel file in a correct TPT column format.</p>

                        {errorMessage ? <p className="mt-3 text-red-600">{errorMessage}</p> : null}

                        {fileName ? (
                            <div className="mt-4 flex flex-wrap items-center gap-3 gap-y-2">
                                <span className="text-sm">
                                    Loaded: <strong>{fileName}</strong> ({rows.length} data rows)
                                </span>
                                <button
                                    type="button"
                                    onClick={handleDownloadJson}
                                    className="rounded bg-sky-600 px-3 py-1 text-white hover:bg-sky-700"
                                    disabled={!rows.length}
                                >
                                    Download JSON
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSaveReport}
                                    className="rounded bg-green-600 px-3 py-1 text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-gray-400"
                                    disabled={!rows.length || client._id === emptyObjectId || savingReport}
                                >
                                    {savingReport ? "Saving report..." : "Save as report"}
                                </button>
                            </div>
                        ) : null}

                        {saveMessage ? <p className="mt-3 text-sm text-gray-700">{saveMessage}</p> : null}
                    </div>
                }
            />

            {marketExposureByCreditRating.length ? (
                <div className="mt-6">
                    <h2 className="mb-2">Sum by Credit Rating</h2>
                    <div className="overflow-auto rounded border border-gray-300 bg-white">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="px-3 py-2">Credit_rating</th>
                                    <th className="px-3 py-2 text-right">Market_exposure_amount_in_portfolio_currency_(B)</th>
                                    <th className="px-3 py-2 text-right">Weighted stress (%)</th>
                                    <th className="px-3 py-2 text-right">Stressed amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {marketExposureByCreditRating.map((row) => (
                                    <tr key={row.creditRating} className="border-t border-gray-200">
                                        <td className="px-3 py-2">{row.creditRating}</td>
                                        <td className="px-3 py-2 text-right">
                                            {row.sum.toLocaleString("sv-SE", { maximumFractionDigits: 2 })}
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            {row.weightedStressPercent === null
                                                ? "-"
                                                : row.weightedStressPercent.toLocaleString("sv-SE", { maximumFractionDigits: 4 })}
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            {row.stressedSum.toLocaleString("sv-SE", { maximumFractionDigits: 2 })}
                                        </td>
                                    </tr>
                                ))}
                                <tr className="border-t-2 border-gray-400 bg-gray-50 font-semibold">
                                    <td className="px-3 py-2">Total</td>
                                    <td className="px-3 py-2 text-right">
                                        {marketExposureTotal.toLocaleString("sv-SE", { maximumFractionDigits: 2 })}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        {marketExposureTotal === 0
                                            ? "-"
                                            : ((stressedTotal / marketExposureTotal) * 100).toLocaleString("sv-SE", {
                                                  maximumFractionDigits: 4
                                              })}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        {stressedTotal.toLocaleString("sv-SE", { maximumFractionDigits: 2 })}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : null}

            <div className="mt-4">
                <h2 className="mb-2">JSON Preview</h2>
                <pre className="max-h-[60vh] overflow-auto rounded border border-gray-300 bg-white p-3 text-xs">{jsonOutput || "[]"}</pre>
            </div>
        </div>
    );
}
